<template>
  <div ref="uploadSection" class="client-hub-upload">
    <v-container class="pa-0">
      <v-row dense>
        <v-col cols="12">
          <div class="client-hub-upload-title">Hub Upload</div>
        </v-col>
      </v-row>
      <v-row v-if="batchListError" dense>
        <v-col cols="12">
          <v-alert
            type="error"
            icon-size="x-small"
            variant="outlined"
            icon="mdi-alert"
            closable
            class="mb-3"
            @click:close="batchListError = ''"
          >
            {{ batchListError }}
          </v-alert>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-autocomplete
            v-model="selectedBatch"
            :loading="fetchBatchListInProgress"
            variant="underlined"
            color="primary"
            :items="batchOptions"
            label="Select Batch"
            placeholder="Select a batch"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-combobox
            v-model="notificationEmails"
            variant="underlined"
            label="Notification Emails"
            placeholder="Add email address"
            hint="Press Enter after each email to add another email."
            multiple
            chips
            closable-chips
            color="primary"
            :error-messages="emailErrors"
            @update:model-value="validateEmails"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="d-flex align-center justify-end">
          <span
            v-if="!isUploadedFilesAvailable && !fetchUploadedFilesInProgress"
            class="text-error pt-2"
            >Please upload the Csv files in the Prepare tab!</span
          >
          <cs-button
            primary
            label="Upload Migration ZIP to Hub"
            left-icon="mdi-upload"
            :loading="uploadInProgress"
            :disabled="isUploadDisabled"
            @click="onRequestToUploadZipClick"
          />
        </v-col>
      </v-row>
    </v-container>
  </div>

  <v-container class="pa-0 mt-6">
    <v-row dense>
      <v-col cols="12">
        <div class="batch-history-header">
          <div class="batch-history-title d-flex align-center gap-2">
            <span class="text-h6 font-weight-medium">Batch History</span>
            <span class="text-caption text-medium-emphasis ml-2"
              >— track and record validation outcomes per batch</span
            >
          </div>
          <v-tooltip location="bottom" max-width="360">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                data-test="batch-history-info-icon"
                size="small"
                class="batch-history-header__info"
                aria-label="What are the Hub migration stages?"
                >mdi-information-outline</v-icon
              >
            </template>
            <div class="batch-history-tooltip">
              <div class="batch-history-tooltip__title">Hub migration stages</div>
              <p><strong>Stage 1</strong> — Hub migrates tenants and external IdPs.</p>
              <p><strong>Stage 2</strong> — Hub migrates product instances and users.</p>
              <p class="batch-history-tooltip__note">
                Both stages are triggered manually by the Hub team after you ask them.
              </p>
            </div>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="hasFailedBatch" dense class="mt-2">
      <v-col cols="12">
        <inline-warning-message message="" class="mb-2">
          <strong>Action Required:</strong> Ideagen Hub has flagged one or more batches as having
          failed. Please review the validation email.
        </inline-warning-message>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="12">
        <v-data-table
          :headers="batchHistoryHeaders"
          :items="sortedBatchList"
          density="compact"
          :items-per-page="10"
          color="primary"
          class="elevation-2 batch-history-table"
        >
          <template #[`item.batchId`]="{ item }">
            <div class="batch-id-cell">
              <span class="batch-id-mono text-truncate" :title="item.batchId">{{
                item.batchId
              }}</span>
              <clipboard-icon-button :value="item.batchId" />
            </div>
          </template>

          <template #[`item.status`]="{ item }">
            <div class="status-cell">
              <v-chip
                v-if="updatingBatchId !== item.batchId"
                :data-test="`status-chip-${item.status}`"
                :style="{
                  backgroundColor: batchStatusConfig[item.status]?.bgColor,
                  color: batchStatusConfig[item.status]?.textColor
                }"
                size="small"
                class="status-chip font-weight-medium"
              >
                <span
                  class="status-chip__dot"
                  :style="{ background: batchStatusConfig[item.status]?.textColor }"
                ></span>
                {{ batchStatusConfig[item.status]?.label ?? item.status }}
              </v-chip>
              <v-progress-circular
                v-else
                indeterminate
                size="16"
                width="2"
                color="primary"
                aria-label="Updating status"
              />
              <span
                v-if="batchStatusConfig[item.status]?.subtext && updatingBatchId !== item.batchId"
                :data-test="`status-subtext-${item.status}`"
                class="status-cell__subtext"
              >
                {{ batchStatusConfig[item.status].subtext }}
              </span>
            </div>
          </template>

          <template #[`item.statusUpdatedAt`]="{ item }">
            {{ formatStatusUpdatedAt(item.statusUpdatedAt) }}
          </template>

          <template #[`item.actions`]="{ item }">
            <row-actions-menu
              :current-status="item.status"
              :disabled="updatingBatchId === item.batchId"
              @update:status="onSetBatchStatus(item, $event)"
              @reupload="onReuploadBatch(item)"
              @cancel="onCancelBatch(item)"
            />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>

  <v-snackbar
    v-model="cancelSnackbar.visible"
    :timeout="5000"
    data-test="cancel-undo-snackbar"
    location="bottom right"
  >
    {{ cancelSnackbar.message }}
    <template #actions>
      <v-btn variant="text" color="primary" data-test="cancel-undo-button" @click="onUndoCancel">
        Undo
      </v-btn>
    </template>
  </v-snackbar>

  <cs-form-dialog
    id="dlg-client-hub-upload-error"
    v-model="errorDialogDisplayed"
    body-id="dlg-client-hub-upload-error-body"
    heading="Hub Upload Error"
    :primary-action="{
      label: 'OK',
      eventName: 'primary-click'
    }"
    @primary-click="errorDialogDisplayed = false"
  >
    <template #cs-form-dialog-content>
      <div class="error-message">{{ uploadErrorMessage }}</div>
    </template>
  </cs-form-dialog>

  <cs-form-dialog
    id="dlg-client-hub-upload-success"
    v-model="successDialogDisplayed"
    body-id="dlg-client-hub-upload-success-body"
    heading="Hub Upload Success"
    :primary-action="{
      label: 'OK',
      eventName: 'primary-click'
    }"
    @primary-click="onSuccessDialogClose"
  >
    <template #cs-form-dialog-content>
      <div class="success-message">{{ uploadSuccessMessage }}</div>
    </template>
  </cs-form-dialog>

  <cs-form-dialog
    id="dlg-client-hub-generate-zip-selected-files"
    v-model="showSelectFileDialog"
    body-id="dlg-client-hub-generate-zip-selected-files-body"
    heading="Files to Upload to Hub"
    :primary-action="{
      label: 'Upload To Hub',
      eventName: 'primary-click',
      disabled: selectedFiles.length === 0
    }"
    :secondary-action1="{
      label: 'Cancel',
      eventName: 'secondary-click'
    }"
    @primary-click="onSelectFilesClick"
    @secondary1-click="showSelectFileDialog = false"
  >
    <template #cs-form-dialog-content>
      <v-list>
        <v-list-item v-for="(file, key) in uploadedFiles" :key="key">
          <v-checkbox
            v-model="selectedFiles"
            :value="file.type"
            :label="file.title"
            :disabled="file.status !== 'success'"
            color="primary"
            hide-details
            density="compact"
          />
          <div v-if="key === 'external_idp_saml' && xmlUploadFiles.length != 0" class="ml-6">
            (<span v-for="(xmlFile, index) in xmlUploadFiles" :key="index">
              <span
                >{{ xmlFile
                }}<span v-if="xmlFile !== xmlUploadFiles[xmlUploadFiles.length - 1]">, </span></span
              > </span
            >)
          </div>
        </v-list-item>
      </v-list>
    </template>
  </cs-form-dialog>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import * as types from '../store/mutationTypes';
import hubMigration from '../lib/const/hubMigration';
import enumMigrationCsvType from '../lib/const/hubMigrationCsvType';
import batchStatus, { batchStatusConfig } from '../lib/const/batchStatus';
import InlineWarningMessage from './InlineWarningMessage';
import ClipboardIconButton from './common/ClipboardIconButton';
import RowActionsMenu from './common/RowActionsMenu';

export default {
  name: 'ClientHubUpload',

  components: {
    InlineWarningMessage,
    ClipboardIconButton,
    RowActionsMenu
  },

  extends: CSBase,

  props: {
    clientSid: { type: String, required: true }
  },

  data() {
    return {
      selectedBatch: hubMigration.NEW_BATCH,
      batchOptions: [],
      notificationEmails: [],
      emailErrors: '',
      uploadInProgress: false,
      fetchBatchListInProgress: false,
      errorDialogDisplayed: false,
      uploadErrorMessage: '',
      successDialogDisplayed: false,
      uploadSuccessMessage: '',
      batchListError: '',
      uploadedFiles: {},
      fetchUploadedFilesInProgress: false,
      showSelectFileDialog: false,
      selectedFiles: [],
      xmlUploadFiles: [],
      batchStatusConfig,
      updatingBatchId: '',
      cancelSnackbar: {
        visible: false,
        message: '',
        previousStatus: null,
        batchId: null
      },
      batchHistoryHeaders: [
        { title: 'Batch ID', key: 'batchId', width: '60%' },
        { title: 'Status', key: 'status', width: '260px' },
        { title: 'Last Updated Date', key: 'statusUpdatedAt', width: '160px' },
        {
          title: 'Actions',
          key: 'actions',
          sortable: false,
          align: 'end',
          cellProps: { class: 'sticky-actions-column' },
          headerProps: { class: 'sticky-actions-column' }
        }
      ]
    };
  },

  computed: {
    uploadedCsvList() {
      return this.$store.getters['hub-migration/uploadedCsvList'];
    },
    batchList() {
      return this.$store.getters['hub-migration/batchList'];
    },
    sortedBatchList() {
      const cancelled = [];
      const active = [];
      for (const batch of this.batchList) {
        if (batch.status === batchStatus.CANCELLED) {
          cancelled.push(batch);
        } else {
          active.push(batch);
        }
      }
      const byDateDesc = (a, b) =>
        new Date(b.statusUpdatedAt || 0) - new Date(a.statusUpdatedAt || 0);
      return [...active.sort(byDateDesc), ...cancelled.sort(byDateDesc)];
    },
    hasFailedBatch() {
      return this.batchList.some((b) => b.status === batchStatus.FAILED);
    },
    isUploadDisabled() {
      return (
        this.uploadInProgress ||
        this.notificationEmails.length === 0 ||
        !this.isUploadedFilesAvailable
      );
    },
    isUploadedFilesAvailable() {
      return Object.values(this.uploadedFiles).some((file) => file.status === 'success');
    }
  },

  watch: {
    uploadedCsvList: {
      handler() {
        this.resetUploadedFiles();
      },
      deep: true
    }
  },

  async mounted() {
    this.uploadedFiles = this.createDefaultUploadedFiles();
    await Promise.all([this.fetchBatchList(), this.fetchUploadedFiles()]);
  },

  methods: {
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    validateEmails(emails) {
      this.notificationEmails = emails.filter((email) => this.isValidEmail(email));
      this.emailErrors = emails.length !== this.notificationEmails.length ? 'Invalid email' : '';
    },
    async fetchBatchList() {
      if (this.fetchBatchListInProgress) return;
      this.fetchBatchListInProgress = true;

      try {
        const response = await this.$store.dispatch('hub-migration/getCsvZipHubUploadBatchList', {
          clientSid: this.clientSid
        });
        this.batchOptions = [hubMigration.NEW_BATCH, ...response.batches];
        this.batchListError = '';
      } catch (err) {
        this.batchListError = `Failed to load existing batches. Please try again later. Error: ${err.message}`;
        this.batchOptions = [hubMigration.NEW_BATCH];
      } finally {
        this.fetchBatchListInProgress = false;
      }
    },
    createDefaultUploadedFiles() {
      return {
        tenants: {
          title: 'Tenant Information',
          type: enumMigrationCsvType.TENANTS
        },
        external_idp_oidc: {
          title: 'External IDP (OIDC) Information',
          type: enumMigrationCsvType.EXTERNAL_IDP_OIDC
        },
        external_idp_saml: {
          title: 'External IDP (SAML) Information',
          type: enumMigrationCsvType.EXTERNAL_IDP_SAML
        },
        product_instances: {
          title: 'Product Instance Information',
          type: enumMigrationCsvType.PRODUCT_INSTANCES
        },
        users: { title: 'User Information', type: enumMigrationCsvType.USERS }
      };
    },
    async fetchUploadedFiles() {
      if (this.fetchUploadedFilesInProgress) return;
      this.fetchUploadedFilesInProgress = true;
      this.xmlUploadFiles = [];
      try {
        const response = await this.$store.dispatch('hub-migration/getCsvList', {
          clientSid: this.clientSid
        });
        if (!response) {
          this.fetchUploadedFilesInProgress = false;
        }
      } catch (error) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      } finally {
        this.fetchUploadedFilesInProgress = false;
      }
    },
    resetUploadedFiles() {
      this.uploadedFiles = this.createDefaultUploadedFiles();
      this.xmlUploadFiles = [];
      const { csv: uploadedCsvFiles = [], saml_xml: uploadedXmlFiles } = this.uploadedCsvList || {};
      const files = this.createDefaultUploadedFiles();
      uploadedCsvFiles.forEach((file) => {
        const baseName = file.fileName.replace(/\.csv$/, '').replace('migration_', '');
        if (files[baseName]) {
          files[baseName].status = 'success';
          files[baseName].lastModified = file.lastModified;
        }
      });
      this.uploadedFiles = files;
      const uploadedXmlNames = (uploadedXmlFiles?.uploaded || []).map((file) => file.fileName);
      this.xmlUploadFiles = uploadedXmlNames || [];
    },
    async onRequestToUploadZipClick() {
      if (this.uploadInProgress) return;

      if (this.notificationEmails.length === 0) {
        this.emailErrors = 'Notification Emails field is required';
        return;
      }
      this.showSelectFileDialog = true;
    },
    onSelectFilesClick() {
      this.showSelectFileDialog = false;
      if (this.selectedFiles.length === 0) {
        this.uploadErrorMessage = 'Please select at least one file to upload';
        return;
      }

      this.uploadZipToHub(this.selectedFiles);
    },
    async uploadZipToHub(selectedCsvTypes) {
      if (this.uploadInProgress) return;
      this.uploadInProgress = true;
      try {
        if (this.selectedBatch === hubMigration.NEW_BATCH) {
          await this.$store.dispatch('hub-migration/postCsvZipHubUpload', {
            clientSid: this.clientSid,
            email: this.notificationEmails,
            selectedCsvTypes
          });
          this.uploadSuccessMessage = 'Successfully created new batch in Hub';
        } else {
          await this.$store.dispatch('hub-migration/patchCsvZipHubUpload', {
            clientSid: this.clientSid,
            batchId: this.selectedBatch,
            email: this.notificationEmails,
            selectedCsvTypes
          });
          this.uploadSuccessMessage = 'Successfully updated batch in Hub';
        }
        this.successDialogDisplayed = true;
        await this.fetchBatchList();
      } catch (error) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.errorDialogDisplayed = true;
        const apiErrorMessage = error?.response?.data?.message || 'Server Error';
        this.uploadErrorMessage = `${error.message} \n ${apiErrorMessage}`;
      } finally {
        this.uploadInProgress = false;
      }
    },
    onSuccessDialogClose() {
      this.successDialogDisplayed = false;
      this.uploadSuccessMessage = '';
    },
    async onSetBatchStatus(row, status) {
      this.updatingBatchId = row.batchId;
      try {
        await this.$store.dispatch('hub-migration/patchCsvZipHubUploadBatchStatus', {
          clientSid: this.clientSid,
          batchId: row.batchId,
          status
        });
        await this.fetchBatchList();
      } catch (error) {
        console.error('[ClientHubUpload] Failed to update batch status:', error);
      } finally {
        this.updatingBatchId = '';
      }
    },
    async onCancelBatch(row) {
      const previousStatus = row.status;
      this.updatingBatchId = row.batchId;
      try {
        await this.$store.dispatch('hub-migration/patchCsvZipHubUploadBatchStatus', {
          clientSid: this.clientSid,
          batchId: row.batchId,
          status: batchStatus.CANCELLED
        });
        this.cancelSnackbar = {
          visible: true,
          message: 'Batch cancelled',
          previousStatus,
          batchId: row.batchId
        };
        await this.fetchBatchList();
      } catch (error) {
        console.error('[ClientHubUpload] Failed to cancel batch:', error);
        this.batchListError = `Failed to cancel batch. ${error.message}`;
      } finally {
        this.updatingBatchId = '';
      }
    },
    async onUndoCancel() {
      if (!this.cancelSnackbar.batchId || !this.cancelSnackbar.previousStatus) return;
      const { batchId, previousStatus } = this.cancelSnackbar;
      this.cancelSnackbar.visible = false;
      this.updatingBatchId = batchId;
      try {
        await this.$store.dispatch('hub-migration/patchCsvZipHubUploadBatchStatus', {
          clientSid: this.clientSid,
          batchId,
          status: previousStatus
        });
        await this.fetchBatchList();
      } catch (error) {
        console.error('[ClientHubUpload] Failed to undo cancel:', error);
      } finally {
        this.updatingBatchId = '';
      }
    },
    onReuploadBatch(row) {
      this.selectedBatch = row.batchId;
      this.$refs.uploadSection?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    },
    formatStatusUpdatedAt(iso) {
      if (!iso) return '—';
      const tz = this.$store.state.common.clientTimezone;
      if (!tz) return '—';
      return this.$app.moment(iso).utc().tz(tz).format('DD/MM/YYYY');
    }
  }
};
</script>

<style scoped>
.client-hub-upload {
  padding: 0px;
}

.client-hub-upload-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
}

.error-message {
  white-space: pre-line;
  color: red;
}

.batch-id-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.batch-id-mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.batch-history-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.batch-history-header__info {
  cursor: help;
  color: #6a6356;
}

.batch-history-tooltip__title {
  font-weight: 600;
  margin-bottom: 6px;
}

.batch-history-tooltip__note {
  margin-top: 8px;
  font-size: 11px;
  opacity: 0.85;
}

.batch-history-title {
  margin-bottom: 0;
}

.status-cell {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  padding-block: 4px;
}

.status-cell__subtext {
  font-size: 11px;
  color: #5a5a5a;
  line-height: 1.4;
}

.status-chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  flex-shrink: 0;
}

:deep(.batch-history-table .v-data-table__td) {
  padding-block: 8px;
}

:deep(.sticky-actions-column) {
  position: sticky !important;
  right: 0;
  background-color: white;
  z-index: 2;
}
</style>
